import React, {useState} from "react";
import {Button, Image, Modal, Skeleton} from "antd";
import {FileTextOutlined, PaperClipOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {TicketPriorityTag} from "../common/TicketPriorityTag";
import {TicketInfoBand} from "./TicketInfoBand";
import {TicketManagementArea} from "./TicketManagementArea";
import {TicketHistoryTabs} from "./TicketHistoryTabs";
import {CloseTicketModal} from "./CloseTicketModal";
import {CancelTicketModal} from "./CancelTicketModal";
import {
    DescriptionBox,
    DetailModal,
    ImageList,
    ImageTile,
    SectionCard,
    SectionTitle,
    TicketNumber,
    TitleIcon,
    TitleRow
} from "./TicketDetail.styles";

const TicketDetailContent = ({
    ticketId,
    readOnly,
    customerView,
    roleOptions,
    roleLabels,
    onChanged
}) => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const [reloadKey, setReloadKey] = useState(0);
    const [closeModalVisible, setCloseModalVisible] = useState(false);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);

    const [loading, response] = useFetch({
        fetchFn: stores.ticketStore.getTicket.bind(null, ticketId),
        defaultDataValue: null,
        dependencies: [ticketId, reloadKey]
    });

    const detail = response?.data;
    const reload = () => {
        setReloadKey(previous => previous + 1);
        onChanged();
    };

    if (!detail) {
        return loading ? <Skeleton active /> : null;
    }

    const {ticket, notes, history, permissions} = detail;
    const can = key => Boolean(permissions?.[key]) && !readOnly;
    // İşletme detayı salt okunurdur; yalnızca iptal beklemedeki talepte açıktır ve not istemeden onayla yapılır
    const canCancel = customerView ? Boolean(permissions?.canCancel) : can("canCancel");
    const confirmCustomerCancel = () =>
        Modal.confirm({
            title: t("detail.customerCancel.title", {ns: "tickets"}),
            content: t("detail.customerCancel.content", {ns: "tickets"}),
            okText: t("detail.customerCancel.confirm", {ns: "tickets"}),
            okButtonProps: {danger: true},
            cancelText: t("detail.customerCancel.cancel", {ns: "tickets"}),
            onOk: async () => {
                if (await stores.ticketStore.cancelTicket(ticket._id, {})) {
                    reload();
                }
            }
        });

    return (
        <>
            <TitleRow>
                <TitleIcon>
                    <FileTextOutlined />
                </TitleIcon>
                <span>{ticket.ticketType?.name}</span>
                {!customerView && <TicketPriorityTag priority={ticket.priority} pill />}
                <TicketNumber>{ticket.number}</TicketNumber>
            </TitleRow>
            <TicketInfoBand ticket={ticket} customerView={customerView} />
            <SectionCard
                size="small"
                title={
                    <SectionTitle>
                        <FileTextOutlined />
                        {t("detail.content", {ns: "tickets"})}
                    </SectionTitle>
                }
                style={{marginBottom: 16}}
            >
                <DescriptionBox>{ticket.description}</DescriptionBox>
            </SectionCard>
            {ticket.photoUrls?.length > 0 && (
                <SectionCard
                    size="small"
                    title={
                        <SectionTitle>
                            <PaperClipOutlined />
                            {`${t("detail.images", {ns: "tickets"})} (${ticket.photoUrls.length})`}
                        </SectionTitle>
                    }
                    style={{marginBottom: 16}}
                >
                    <ImageList>
                        <Image.PreviewGroup>
                            {ticket.photoUrls.map((url, index) => (
                                <ImageTile key={`${url}-${index}`}>
                                    <Image
                                        src={url}
                                        width="100%"
                                        height="100%"
                                        style={{objectFit: "contain", borderRadius: 4}}
                                    />
                                </ImageTile>
                            ))}
                        </Image.PreviewGroup>
                    </ImageList>
                </SectionCard>
            )}
            {!customerView && (
                <TicketManagementArea
                    ticket={ticket}
                    can={can}
                    roleOptions={roleOptions}
                    assignTargets={permissions?.assignTargets}
                    onChanged={reload}
                />
            )}
            <TicketHistoryTabs
                ticket={ticket}
                history={history}
                notes={notes}
                roleLabels={roleLabels}
                customerView={customerView}
                canAddNote={can("canAddNote")}
                onChanged={reload}
            />
            {(canCancel || can("canClose")) && (
                <div style={{display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16}}>
                    {canCancel && (
                        <Button
                            danger
                            onClick={
                                customerView
                                    ? confirmCustomerCancel
                                    : () => setCancelModalVisible(true)
                            }
                        >
                            {t("detail.actions.cancel", {ns: "tickets"})}
                        </Button>
                    )}
                    {can("canClose") && (
                        <Button type="primary" onClick={() => setCloseModalVisible(true)}>
                            {t("detail.actions.close", {ns: "tickets"})}
                        </Button>
                    )}
                </div>
            )}
            <CancelTicketModal
                ticket={ticket}
                visible={cancelModalVisible}
                onCancel={() => setCancelModalVisible(false)}
                onCancelled={() => {
                    setCancelModalVisible(false);
                    reload();
                }}
            />
            <CloseTicketModal
                ticket={ticket}
                visible={closeModalVisible}
                onCancel={() => setCloseModalVisible(false)}
                onClosed={() => {
                    setCloseModalVisible(false);
                    reload();
                }}
            />
        </>
    );
};

export const TicketDetailModal = ({ticketId, onClose, ...props}) => {
    const {t} = useTranslation(["tickets"]);

    return (
        <DetailModal
            title={t("detail.title", {ns: "tickets"})}
            visible={!!ticketId}
            onCancel={onClose}
            footer={null}
            width={820}
            destroyOnClose
        >
            {ticketId && <TicketDetailContent key={ticketId} ticketId={ticketId} {...props} />}
        </DetailModal>
    );
};
